import { FunctionComponent, useState } from "react";

import { faRotateLeft } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { useSnackBar } from "@/utils/useSnackbar";

import { useRestoreSalesforceDefaultFieldMappingsMutation } from "@/pages/Private/redux/salesforce/salesforce.api";

import { SalesforceEntityType } from "../schema/salesforce";

interface SalesforceRestoreDefaultsButtonProps {
	/** The tab's sObject — only its defaults are restored, not all four. */
	entityType: SalesforceEntityType;
	ts: (key: string, options?: Record<string, unknown>) => string;
}

/**
 * Re-adds the out-of-the-box mappings of one tab — the same set a fresh Salesforce connection
 * is seeded with. The endpoint only fills gaps, so this never overwrites or duplicates a mapping
 * the customer configured themselves; when nothing was missing it returns an empty list, which
 * is reported rather than left as a silent no-op.
 */
export const SalesforceRestoreDefaultsButton: FunctionComponent<
	SalesforceRestoreDefaultsButtonProps
> = ({ entityType, ts }) => {
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();
	const [restoreDefaults] = useRestoreSalesforceDefaultFieldMappingsMutation();
	const [isRestoring, setIsRestoring] = useState(false);

	const handleRestore = async () => {
		setIsRestoring(true);

		try {
			const created = await restoreDefaults({ entityType }).unwrap();

			if (!created.length) {
				handleOpenSnackbar(ts("restoreDefaultsNoneAdded"));
			}
		} catch {
			handleOpenSnackbar(ts("restoreDefaultsFailed"));
		} finally {
			setIsRestoring(false);
		}
	};

	return (
		<>
			{renderMessageSnackbar()}
			<span title={ts("restoreDefaultsTooltip")}>
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					image={<Icon className="mr-2 h-[16px] w-[16px] text-text" icon={faRotateLeft} />}
					isLoading={isRestoring}
					size={ButtonSize.S}
					testId="salesforce-restore-defaults"
					title={ts("restoreDefaults")}
					onClick={handleRestore}
				/>
			</span>
		</>
	);
};
